// Generic platform model: record types, reports, screens, agent tools, rules, services and environments.
export default {
  generic: {
    stats: {
      total: "Definitions", totalHint: "Keys in this catalogue",
      published: "Live", publishedHint: "Have a published version",
      drafts: "Drafts", draftsHint: "Open drafts or in review",
    },
    search: "Search by name or key",
    filters: { unpublished: "Never published" },
    noMatch: "Nothing matches these filters.",
    createIntro: "Give it a name and a key. Everything except the key can change later.",
    keyHint: "Lower-case letters, digits, dots and hyphens. Used in links and packages; cannot change.",
    createOpen: "Create and open",
    created: "Created. Version 1 is a draft.",
    notFound: "Not found",
    notFoundBody: "Nothing has the key “{{key}}”. It may have been discarded.",
    publishedNotice: "This version is live. Create a new version to change it; this one stays live until the new one is published.",
    stageBody: "The draft joins your current release. It goes live when an approver publishes the release, and can be rolled back from Releases.",
    stageTitle: "Publish {{name}}",
    tabs: { code: "Code", versions: "Versions" },
    lint: { name: "Give it a name." },
    condition: {
      always: "always",
      custom: "a custom condition (see JSON)",
      match: "Match",
      allOf: "all of these",
      anyOf: "any of these",
      and: "and",
      or: "or",
      field: "Field",
      operator: "Condition",
      value: "Value",
      add: "Add condition",
      json: "JSON",
      simple: "Back to simple view",
      ops: {
        eq: "is", neq: "is not", gt: "is above", gte: "is at least", lt: "is below", lte: "is at most",
        empty: "is empty", filled: "is filled in", contains: "contains", oneOf: "is one of",
      },
    },
  },
  records: {
    referenceHint: "Record number or id ({{type}})",
  },
  reports: {
    title: "Reports",
    subtitle: "Tables and charts over records or cases, defined without code, run on live data and exported as CSV or Excel.",
    new: "New report",
    emptyTitle: "No reports yet",
    emptyBody: "Create a report over a record type or the cases of a service: counts by month, totals by sector, or a filtered list.",
    help: {
      what: "A report reads the records of a type or the cases of a service, keeps the rows that match its filter, and either lists them or groups them with counts, totals and averages.",
      export: "Reports respect department visibility: each person sees the rows they may see. Every export is written to the audit trail.",
    },
    summary: "Source: {{source}} · {{chart}}",
    tabs: { design: "Design", results: "Results" },
    source: {
      title: "Source", intro: "Where the rows come from.", kind: "Rows are", records: "Records of a type", cases: "Cases of a service",
      recordType: "Record type", service: "Service", includeTest: "Include test records and cases",
    },
    filter: { title: "Filter", intro: "Keep only the rows that match. Leave empty for every row." },
    shape: {
      title: "Layout", intro: "List rows with chosen columns, or group them and calculate figures.",
      list: "List", grouped: "Grouped", groupBy: "Group by", thenBy: "Then by (optional)", measures: "Figures", fn: "Calculation", field: "Field",
      addMeasure: "Add figure", columns: "Columns", columnsHint: "Leave all unticked to show the first eight fields.", chart: "Chart",
    },
    bucket: { month: "by month", year: "by year" },
    fns: { count: "Count", sum: "Total", avg: "Average", min: "Lowest", max: "Highest" },
    charts: { table: "Table only", bar: "Bar chart", line: "Line chart", pie: "Pie chart", kpi: "Headline figures" },
    caseFields: { caseNumber: "Case number", title: "Title", state: "Stage", status: "Status", priority: "Priority", createdAt: "Created", workspaceKey: "Department", isTest: "Test" },
    preview: { title: "Preview", intro: "Runs this draft on live data. Nothing is saved.", run: "Run", empty: "Pick a source to preview the report." },
    results: { title: "Results", published: "Published version {{version}} on live data.", latest: "Latest version on live data.", refresh: "Refresh" },
    lint: {
      source: "Pick where the rows come from.",
      measureField: "Figure “{{name}}” needs a field.",
      chartNeedsGroup: "Charts need grouped rows. Group the rows or choose “Table only”.",
    },
  },
  recordTypes: {
    title: "Data",
    subtitle: "Record types define the data a department keeps – fields, links and checks – without code or database changes. Every record is checked on the server when it is saved.",
    new: "New record type",
    emptyTitle: "No record types yet",
    emptyBody: "Create a record type for any register a department keeps: studies, licences, inspections or complaints.",
    help: {
      what: "A record type is a list of fields with their checks. Publishing it creates a register staff can add records to, search and report on. No table is created: every record is kept in one shared, indexed store.",
      checks: "Checks run on the server with the same engine as submitted forms, so a record that breaks a rule is refused whichever screen or system writes it.",
      extension: "A record type can extend a service: each case of that service then carries one record of the type, so a department adds its own fields to shared cases.",
    },
    stats: { records: "Records", recordsHint: "Kept across all types" },
    columns: { fields: "Fields", records: "Records", owner: "Owner" },
    shared: "Shared by all departments",
    extends: "Extends {{service}}",
    summary: "{{fields}} fields · {{links}} links · {{checks}} checks · numbers start {{prefix}}",
    tabs: { fields: "Fields", links: "Links", checks: "Checks", settings: "Settings", try: "Try a record", records: "Records" },
    types: { text: "Text", longText: "Long text", number: "Number", money: "Amount (AED)", date: "Date", boolean: "Yes / no", choice: "Choice", email: "Email", reference: "Link to a record" },
    typeHelp: {
      text: "A single line of text. Add a maximum length or a pattern to check its format.",
      longText: "Several lines of text, such as a description or a summary.",
      number: "A number. Add a minimum and maximum to refuse values outside the range.",
      money: "An amount in dirhams. It cannot be negative unless a minimum says otherwise.",
      date: "A calendar date.",
      boolean: "A yes / no switch.",
      choice: "One value from a fixed list of options.",
      email: "An email address, checked for its format.",
      reference: "The number of a record of another type, checked to exist.",
    },
    fields: {
      intro: "The order here is the order on screens. Select a field to edit it.",
      none: "No fields yet.",
      newLabel: "Field {{n}}",
      add: "Add field",
      type: "Type",
      label: "Label",
      labelAr: "Label in Arabic",
      keyHint: "Used in checks, reports and exports.",
      help: "Help text",
      helpAr: "Help text in Arabic",
      min: "Minimum",
      max: "Maximum",
      maxLength: "Maximum length",
      pattern: "Format (regular expression)",
      patternHint: "Optional, e.g. ^[0-9]{6}$ for a six-digit number.",
      target: "Record type it links to",
      options: "Options",
      optionValue: "Stored value",
      addOption: "Add option",
      required: "Required",
      searchable: "Searchable",
      listed: "Shown in lists",
      useAsTitle: "Use as the record title",
      title: "Title",
      up: "Move up",
      down: "Move down",
      emptyTitle: "Add the first field",
      emptyBody: "Pick a type and add a field. You can reorder, rename and remove fields until the type is published.",
    },
    links: {
      intro: "Links connect a record to cases or to records of other types, so a register can be browsed from either side.",
      none: "No links yet.",
      add: "Add link",
      target: "Links to",
      case: "A case",
      many: "Several allowed",
    },
    checks: {
      intro: "A check refuses a record, or warns, when its condition is met. Checks can compare several fields.",
      none: "No checks yet. Field-level checks (required, minimum, format) apply without adding anything here.",
      add: "Add check",
      flagWhen: "Flag the record when",
      message: "Message",
      messageAr: "Message in Arabic",
      severityLabel: "Effect",
      severity: { error: "Refuse", warning: "Warn" },
    },
    settings: {
      about: "About",
      numbering: "Numbers and ownership",
      numberingHint: "Records are numbered automatically, e.g. {{example}}.",
      prefix: "Number prefix",
      titleField: "Title of a record",
      titleFieldHint: "Shown in lists and search results.",
      numberAsTitle: "The record number",
      owner: "Owning department",
      ownerHint: "Staff see records of their own departments and shared records.",
      extends: "Extends the cases of a service",
      extendsHint: "Each case of the service carries one record of this type, filled in on the case.",
      tags: "Tags",
    },
    try: {
      title: "Try a record",
      intro: "Fill in a sample record and check it on the server against this version's fields and checks. Nothing is saved.",
      check: "Check on the server",
      saveTest: "Save as a test record",
      saved: "Test record {{number}} saved.",
      publishFirst: "Test records can be saved once a version is published.",
      needFields: "Add fields before trying a record.",
      result: "Result",
      noResult: "The result of the server check appears here.",
      valid: "The record passes every check",
      validBody: "The server would accept this record.",
      invalid: "Problems found: {{count}}",
    },
    records: {
      intro: "Records kept in this type: {{count}}.",
      includeTest: "Include test records",
      new: "New record",
      none: "No records yet.",
      number: "Number",
      titleCol: "Title",
      test: "Test",
      archived: "Archived",
      saved: "Record {{number}} saved.",
      archive: "Archive",
      restore: "Restore",
      notPublished: "Not published yet",
      notPublishedBody: "Records can be added once the first version of this type is published.",
    },
    lint: {
      fieldKey: "Field key “{{key}}” must start with a lower-case letter and use letters, digits or _.",
      fieldTwice: "The key “{{key}}” is used twice.",
      fieldLabel: "Field “{{key}}” needs a label.",
      options: "Choice field “{{key}}” needs at least one option.",
      target: "Link field “{{key}}” needs the record type it points at.",
      range: "Field “{{key}}”: the minimum is above the maximum.",
      linkKey: "Link key “{{key}}” must start with a lower-case letter and use letters, digits or _.",
      linkTarget: "Link “{{key}}” needs a target.",
      prefix: "The number prefix must be 2–8 capital letters or digits, starting with a letter.",
      titleField: "The title field is not one of the fields.",
      checkMessage: "Check “{{key}}” has no message.",
      noFields: "Add at least one field before publishing.",
    },
  },
  serviceDesigner: {
    title: "Services",
    subtitle: "Every process the platform runs. A new one is a form, a workflow, a case number and roles put together here, then released – no code.",
    new: "New service",
    emptyTitle: "No services yet",
    emptyBody: "A service ties an intake form and a workflow to a department, its roles and its case numbers.",
    help: {
      what: "A service is one process: what applicants or staff start, which form collects the request, which workflow runs it and who works it.",
      dynamic: "Dynamic services are assembled here from published forms and workflows and open generic cases. Compiled services are the procedures built in code; their catalogue card, documents and roles are still edited here.",
      release: "Changes go live through a release: stage the draft, have it approved, and roll it back from Releases if needed.",
    },
    stats: { open: "Open cases", openHint: "Across every service" },
    columns: { kind: "Kind", code: "Code", category: "Category", workflow: "Workflow", cases: "Open / all cases" },
    kind: { Dynamic: "Dynamic", Compiled: "Compiled" },
    audience: { External: "Applicants (portal)", Internal: "Staff only", Any: "Applicants and staff" },
    summary: "{{kind}} · code {{code}} · workflow {{workflow}}",
    compiledTitle: "Compiled procedure",
    compiledBody: "This service runs the built-in {{type}} procedure. Its card, documents, roles and number pattern can change here; its steps are fixed in code.",
    tabs: { overview: "Overview", process: "Process", documents: "Documents", access: "Department and roles" },
    identity: { title: "Identity", intro: "How the service is named and grouped in the catalogue." },
    card: { title: "Catalogue card", intro: "What applicants read before they start." },
    fields: {
      category: "Category", categoryAr: "Category (Arabic)", audience: "Who starts it", icon: "Icon", portalVisible: "Shown on the portal", sortOrder: "Order",
      summary: "Summary", summaryAr: "Summary (Arabic)", legalBasis: "Legal basis", legalBasisAr: "Legal basis (Arabic)", processingTime: "Processing time", processingTimeAr: "Processing time (Arabic)",
      workflow: "Workflow", form: "Intake form", rules: "Eligibility rules", automations: "Automations", agents: "Agents allowed on its cases",
      code: "Code", codeHint: "2–6 capital letters or digits; unique across services.", pattern: "Case number pattern",
      fees: "Fees", templates: "Letter templates", department: "Department", flag: "Portal feature flag", accessRoles: "Roles that see and work its cases", startRoles: "Roles that may start it",
    },
    process: {
      title: "Form and workflow", intro: "The form collects the request; the workflow's states, tasks, rules and deadlines run it.",
      live: "Live – the published version runs new cases.", notLive: "Not published yet – publish it in Workflow Studio before this service goes live.", pickWorkflow: "Pick a workflow, or create one in Workflow Studio.",
      draft: "draft", openWorkflow: "Workflow Studio", openForm: "Form Builder", noForm: "No intake form", formHint: "Published forms from the Form Builder.", formDraft: "This form has no published version yet.",
      rulesHint: "Checked when a request is submitted; a failing rule stops it with the rule's message.", none: "None",
    },
    numbers: { title: "Case numbers", intro: "Each new case gets the next number in this pattern.", tokens: "{code} {yyyy} {yy} {mm} and one {seq:0000}", examples: "Next numbers:" },
    fees: { title: "Fees and letters", intro: "Fee schedules charged at submission and the letters its cases send.", free: "Free of charge" },
    documents: { title: "Required documents", intro: "The checklist applicants see; required ones must be attached before submission.", none: "No documents requested.", name: "Document", nameAr: "Document (Arabic)", required: "Required", add: "Add document" },
    access: {
      department: "Department", departmentIntro: "The department that owns the service. Its cases, records and reports are kept with that department.", shared: "Shared (every department)",
      flagHint: "Optional. While the flag is off, the portal keeps the previous intake for this service.",
      roles: "Roles", rolesIntro: "Department roles first; roles of other departments are hidden once a department is set.",
      accessHint: "Empty means every staff role.", startHint: "For staff-started services. Empty means every staff role.", everyone: "Every staff role",
    },
    lint: {
      code: "The code must be 2–6 capital letters or digits.",
      pattern: "The case number pattern needs a {seq:0000} token.",
      workflow: "No workflow yet – the service cannot be published without one.",
      form: "Applicants need an intake form to start this service on the portal.",
      document: "Each document needs a name and a key.",
      documentTwice: "Two documents share the same key.",
    },
  },
  rules: {
    title: "Rules",
    subtitle: "The conditions workflows, services and automations check. Written as a decision table or an expression, and tested before they go live.",
    new: "New rule",
    emptyTitle: "No rules yet",
    emptyBody: "A rule answers one question about a case – may this step happen, is the applicant eligible, what is the fee – from the case's own data.",
    help: {
      what: "Workflows use a published rule as a condition on a step (rule:key). Services check eligibility rules when a request is submitted. Automations use them as conditions.",
      table: "A decision table is read top to bottom: the first row whose cells all match gives the outcome, otherwise the last line applies.",
      tests: "Each rule carries its own test cases. A rule cannot be published until every test case passes.",
    },
    stats: { tested: "Tests passing", testedHint: "Rules whose last test run passed" },
    columns: { kind: "Kind", form: "Written as", tests: "Last test run", service: "Service" },
    platformWide: "Platform-wide",
    kinds: { Guard: "Workflow condition", Eligibility: "Eligibility", Condition: "Automation condition", Validation: "Form check", Computation: "Calculation" },
    kindHints: {
      Guard: "Offered in Workflow Studio as a condition on a step.",
      Eligibility: "Checked when a request to the service is submitted.",
      Condition: "Used by automations to decide whether to act.",
      Validation: "A cross-field check on a form.",
      Computation: "Gives a value (a fee, a priority) rather than yes or no.",
    },
    modes: { table: "Decision table", logic: "Expression" },
    fields: { kind: "Kind", mode: "Written as", service: "Service", explanation: "Explanation", explanationAr: "Explanation (Arabic)" },
    summary: "{{kind}} · test cases: {{tests}}",
    tabs: { logic: "Logic", tests: "Test cases", case: "Try on a case", settings: "Settings" },
    ops: { any: "any", eq: "is", neq: "is not", gt: "above", gte: "at least", lt: "below", lte: "at most", between: "between", oneOf: "one of", contains: "contains", empty: "is empty", filled: "is filled" },
    table: {
      intro: "One column per value the rule reads, one row per case. The first matching row decides.",
      input: "Input", operator: "Operator", holds: "Holds?", result: "Result", yes: "Yes", no: "No", otherwise: "Otherwise (no row matches)",
      addRow: "Add row", addInput: "Add input", up: "Move up", down: "Move down", commaList: "a, b, c",
      compiled: "Saved as one expression the workflow engine evaluates; the table stays editable here.",
    },
    logic: { intro: "Conditions over the case's data, all or any of which must hold.", json: "Show as JSON", visual: "Show as conditions" },
    tests: {
      title: "Test cases", intro: "Example cases and the outcome the rule must give. All must pass before publishing.",
      add: "Add test case", run: "Run tests", none: "No test cases yet.", name: "Name", caseName: "Case {{n}}",
      expected: "Expected", input: "Case data (JSON)", passed: "Passed", got: "Gave {{value}}",
      summary: "{{passed}} of {{total}} passed", allPass: "Every test case gives the expected outcome.", someFail: "Some test cases give a different outcome. The rule cannot be published until they pass.",
    },
    case: {
      title: "Try on a real case", intro: "Evaluates this version (saved or not) against a case's data, exactly as the workflow would, and lists the values it read.",
      check: "Check", notFound: "No case with that number.", holds: "The rule holds for {{number}}.", fails: "The rule does not hold for {{number}}.", value: "For {{number}} the rule gives {{value}}.",
      field: "Field", valueCol: "Value on the case",
    },
    settings: {
      title: "Settings", intro: "What kind of rule it is, which service it belongs to and what staff read when it blocks a step.",
      serviceHint: "Limits the field list to that service's form. Platform-wide rules see only case fields.",
      explanationHint: "Shown next to a blocked step.",
      usage: "Workflows refer to this rule as {{key}}.",
    },
    lint: { inputs: "The table needs at least one input.", rows: "The table needs at least one row.", noTests: "No test cases – the rule cannot be published without at least one." },
  },
  tools: {
    title: "Agent tools",
    subtitle: "Formulas, lookups and calls to other systems, defined once and granted to any agent as tool.<key>. Each is tested before agents may use it.",
    new: "New tool",
    emptyTitle: "No tools yet",
    emptyBody: "A tool gives agents an exact answer they would otherwise guess: a fee from a formula, a threshold from a reference table, a figure from another system.",
    summary: "{{kind}} · inputs: {{inputs}} · test cases: {{tests}}",
    help: {
      what: "Agents call a published tool during a run. The figures it returns may be quoted in the draft; figures from anywhere else are flagged by the number guardrail.",
      kinds: "Formula: an expression over typed inputs. Lookup: finds records of a type by field values. Connector: one read-only operation of a published connector.",
      tests: "Formula and lookup tools need at least one test case, and every case must pass before the tool can be published.",
    },
    kinds: { formula: "Formula", lookup: "Lookup", connector: "Connector" },
    inputTypes: { number: "Number", text: "Text", boolean: "Yes/no", date: "Date" },
    lookupOps: { eq: "equals", contains: "contains", lt: "is below", lte: "is at most", gt: "is above", gte: "is at least" },
    tabs: { definition: "Definition", tests: "Tests" },
    fields: { kind: "Kind", purpose: "What the tool does", purposeHint: "The agent reads this to decide when to call the tool.", output: "What it returns", outputHint: "Unit or meaning of the result, e.g. \"fee in AED\". The agent reads it with the result." },
    columns: { kind: "Kind", grant: "Grant", tests: "Tests" },
    stats: { tested: "Passing tests", testedHint: "Tools whose last test run passed" },
    purpose: { title: "Purpose", intro: "The agent reads this to decide when to call the tool. Grant it to an agent as {{key}}." },
    inputs: { title: "Inputs", intro: "What the agent must supply. Numbers and dates are checked before the tool runs.", add: "Add input", none: "No inputs.", label: "Label", key: "Key", type: "Type", required: "Required", remove: "Remove" },
    formula: {
      title: "Formula",
      intro: "A JsonLogic expression over the inputs, or an object of named results each with its own expression.",
      invalid: "This is not valid JSON yet; the last valid formula is kept.",
      help: "Inputs available as {\"var\": \"key\"}: {{vars}}. Example: {\"*\": [{\"var\": \"turnover\"}, 0.001]}.",
    },
    lookup: {
      title: "Lookup",
      intro: "Finds records of one type whose fields match the inputs, and returns the chosen fields.",
      recordType: "Record type", limit: "Most records returned", match: "Match on", addMatch: "Add match",
      field: "Record field", op: "Condition", input: "Input", returns: "Fields returned", returnsHint: "None selected returns every field.",
    },
    connector: {
      title: "Connector operation",
      intro: "The tool calls one operation of a published connector. Its parameters become the inputs.",
      operation: "Operation",
      readOnly: "Only read-only operations are offered: agents read, and changes go through an approved action.",
    },
    try: { title: "Try it", intro: "One call with these inputs, the way an agent would make it.", run: "Run", ok: "Result", failed: "The call failed" },
    tests: {
      title: "Test cases", intro: "Each case gives inputs and the result expected. All must pass before the tool goes live.",
      add: "Add case", run: "Run all", none: "No test cases yet.", caseName: "Case {{n}}", name: "Name", input: "Input", expected: "Expected result",
      expectedHint: "A number or text, or an object listing only the fields to check (e.g. {\"count\": 1}).",
      summary: "{{passed}} of {{total}} passed", allPass: "Every case passes.", someFail: "Some cases fail; the tool cannot be published until they pass.",
      passed: "Passed", got: "Result was {{value}}",
    },
    lint: {
      purpose: "Describe what the tool does.", expression: "The formula needs an expression.", recordType: "Choose the record type to search.",
      operation: "Choose the connector operation.", inputKey: "Every input needs a key.", noTests: "No test cases: the tool cannot be published without at least one.",
    },
  },
  screens: {
    title: "Screens",
    subtitle: "Pages built from blocks – record lists, forms and details, reports and figures, service intake and case views – shown in the back office or the portal at /s/<key>.",
    new: "New screen",
    emptyTitle: "No screens yet",
    emptyBody: "A screen gives a department its own page without code: a register of its records, an intake form for a new service, or a dashboard of its reports.",
    summary: "{{surface}} · {{kind}} · blocks: {{count}}",
    help: {
      what: "A screen is an ordered list of blocks. Each block shows live data: records of a type, a report, a service's cases, or the intake form that starts a service.",
      portal: "Portal screens may only show headings, text, service intake and the applicant's own cases. A portal service must be published and open to applicants.",
      release: "Screens go live like every other definition: a release, a second approver, then publishing. The menu entry appears as soon as the screen is published.",
    },
    fields: { surface: "Shown in", kind: "Kind", recordType: "Record type", service: "Service" },
    surfaces: { backoffice: "Back office", portal: "Portal" },
    kinds: { list: "List", detail: "Detail", dashboard: "Dashboard", form: "Form" },
    tabs: { design: "Blocks", settings: "Menu and access" },
    blocks: {
      heading: { name: "Heading", hint: "Title and one line under it." },
      text: { name: "Text", hint: "A paragraph of guidance." },
      recordList: { name: "Record list", hint: "Searchable, paged list of records, with an optional New button." },
      recordForm: { name: "Record form", hint: "Creates a record; the record type's checks run on save." },
      recordDetail: { name: "Record detail", hint: "The record picked in the list, with its links." },
      report: { name: "Report", hint: "A published report as a table or chart." },
      kpi: { name: "Figure", hint: "One number from a report. Figures next to each other share a row." },
      serviceStart: { name: "Service intake", hint: "The service's published intake form; submitting opens a case." },
      caseList: { name: "Case list", hint: "Cases of a service. On the portal: the applicant's own." },
      caseDetail: { name: "Case detail", hint: "The case picked in the list: stages, next steps, tasks, timeline and documents." },
    },
    moveUp: "Move up",
    moveDown: "Move down",
    noBlocks: "No blocks yet. Add the first one below.",
    addBlock: "Add block",
    notPublished: "Not published yet: the screen goes live with it, in the same release or after it.",
    draft: "draft",
    arabic: "Arabic",
    preview: {
      title: "Live preview",
      backoffice: "As officers will see it, with live data. Forms do not submit in the preview.",
      portal: "As applicants will see it. The preview reads data with your staff access; applicants see only their own cases.",
    },
    props: {
      heading: "Heading", subtitle: "Subtitle", text: "Text", title: "Title", label: "Label", intro: "Introduction",
      pageSize: "Rows per page", columns: "Columns", columnsHint: "None ticked: the fields marked 'show in lists' on the record type.",
      allowCreate: "Show a New button", fields: "Fields", fieldsHint: "None ticked: every field.", submitLabel: "Button label",
      showLinks: "Show links to other records and cases", report: "Report", column: "Column", columnHint: "Key of the report column; empty for the first number column.",
      showActions: "Next steps (workflow buttons)", showTasks: "Tasks", showTimeline: "Timeline", showDocuments: "Documents",
    },
    settings: {
      whereTitle: "Where it appears",
      whereIntro: "The screen opens at {{path}} in the back office or the portal.",
      portalNote: "On the portal the screen appears in the top menu for signed-in applicants.",
      menuTitle: "Menu",
      menuIntro: "Published screens appear in the menu of their surface, in the order below.",
      showInMenu: "Show in the menu",
      icon: "Icon",
      order: "Order",
      orderHint: "Lower numbers come first.",
      accessTitle: "Who sees it",
      accessIntro: "Staff holding one of the ticked roles see the screen. Administrators always see it.",
      accessAll: "No role ticked: every officer sees the screen.",
      accessSome: "Roles ticked: {{count}}.",
    },
    lint: {
      noBlocks: "The screen needs at least one block.",
      portalBlock: "Block {{n}} ({{block}}) cannot be shown on the portal.",
      missingRef: "Block {{n}} ({{block}}) needs its record type, report or service.",
      emptyText: "Block {{n}} has no text.",
      detailWithoutList: "A record detail block shows the record picked in a list; the screen has no record list.",
      caseDetailWithoutList: "A case detail block shows the case picked in a list; the screen has no case list.",
    },
  },
  environments: {
    title: "Environments",
    subtitle: "Changes move from development to staging to production. Each environment approves what it receives, and each can roll back on its own.",
    help: {
      path: "A published release can be promoted to the next environment on the path. It arrives there as a release waiting for approval by that environment's own approvers.",
      signing: "Every promotion is signed with the trust key shared between environments, so an environment only accepts packages from the one before it.",
      rollback: "A release that arrived by promotion can be rolled back in this environment without touching the others.",
    },
    thisOne: "This environment",
    trustKey: "Trust key",
    promoteTitle: "Promote a published release",
    promoteIntro: "Send a published release to {{env}}. It will wait there for approval.",
    lastEnvironment: "This is the last environment on the path, so there is nowhere further to promote to.",
    noPublished: "No published releases yet.",
    releaseMeta: "Items: {{count}} · published {{date}}",
    alreadyPromoted: "Promoted",
    promoteTo: "Promote to {{env}}",
    promoted: "Sent to {{env}} for approval.",
    promoteFailed: "Promotion failed: {{error}}",
    matrixTitle: "Versions per environment",
    matrixIntro: "The published version of every promoted definition in each environment. Each environment numbers its own versions; green means all of them run the same content.",
    matrixEmpty: "Nothing has been promoted yet.",
    present: "Present",
    definition: "Definition",
    unreachable: "{{env}} could not be reached: {{error}}",
    refresh: "Refresh",
    historyTitle: "Promotion history",
    historyIntro: "Promotions sent from this environment and received into it, with the package fingerprint.",
    noHistory: "No promotions yet",
    noHistoryBody: "Promote a published release to see it here.",
    columns: { release: "Release", path: "From → to", items: "Contents", status: "Status", by: "Requested by" },
    evaluationsCarried: "Agent test results carried: {{count}}",
    status: { Sent: "Sent", Received: "Waiting for approval", Published: "Live", RolledBack: "Rolled back", Failed: "Failed", Rejected: "Rejected" },
    rollbackHere: "Roll back here",
    review: "Review",
    rollbackTitle: "Roll back in {{env}}",
    rollbackIntro: "The definitions in this release go back to the versions that were live before it arrived. Other environments are not affected.",
    reason: "Reason",
    rolledBack: "Rolled back in {{env}}.",
    editTitle: "Edit {{env}}",
    editIntro: "Where this environment is reached and which key it trusts.",
    saved: "Environment saved.",
    fields: { name: "Name", nameAr: "Arabic name", baseUrl: "Address", trustKey: "Trust key", trustKeyHint: "A secret reference such as secret://promotion.trust. The key itself stays in the secret store.", order: "Position on the path", enabled: "Enabled" },
    promoteFromRelease: "Promote to {{env}}",
    promoteFromReleaseHint: "Send this release to {{env}}, where it waits for approval.",
  },
} as const;
